import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {
    Positionable,
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {Game} from "../Game";
import {SymbolContainer} from "./SymbolContainer";
import {gsap} from "gsap";
import {Config} from "../config/Config";
import {Mental2SymbolStates} from "../enums/Enum";
import {GameUtils} from "../utils/GameUtils";


export class ReelsView extends PIXI.Container implements StageLayer, SimpleReelModelDelegate {
    private _reelModels: SimpleReelModel[];
    private _reelContainer: PIXI.Container;
    private _reels: PIXI.Container[] = [];
    private _frame: PIXI.Sprite;
    private _deadSymArr: string[] = [];
    private _reelMask: PIXI.Graphics;

    constructor() {
        super();
        this.init();
    }

    public doActivateDeadSymbols(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._deadSymArr.forEach((str: string) => {
            const reelPos: string[] = str.split("_");
            const symbolContainer: SymbolContainer = GameUtils.getSymbol(Number(reelPos[0]), Number(reelPos[1]));
            tl.add(symbolContainer.doActiveDeadSym(), 0);
        });
        this.resetDeadSymArr();
        return tl;
    }

    public resetDeadSymArr(): void {
        this._deadSymArr.length = 0;
    }

    public addDeadSymInfo(value: string): void {
        this._deadSymArr.push(value);
    }

    createSymbol(symbolName: string, state: SimpleReelModelState, symbolPosition: SimpleReelModelSymbolPosition): Positionable {
        const symbolState: Mental2SymbolStates = state == SimpleReelModelState.SPIN ? Mental2SymbolStates.SPIN : Mental2SymbolStates.NORMAL;
        const symbol: SymbolContainer = new SymbolContainer(symbolName, symbolState, symbolPosition.reelID, symbolPosition.position);
        symbol.position.x = symbolPosition.reelID * Config.SYMBOL_WIDTH + Config.SYMBOL_WIDTH * 0.5;
        this._reels[symbolPosition.reelID].addChild(symbol);
        return symbol;
    }

    removeSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void {
        (<PIXI.spine.Spine>symbolData.positionable).destroy();
    }

    getVisibleSymbolAt(column: number, row: number): SymbolContainer {
        const visibleSymbols = this._reelModels[column].getAllVisibleStopSymbols();
        return <SymbolContainer>visibleSymbols[row].positionable;
    }

    public show(duration: number = 0.2, alpha: number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this.alpha = 0;
        this.visible = true;
        tl.add([
            Game.enhancerView.show(duration, alpha),
            Game.foregroundElements.show(duration, alpha),
            gsap.to(this, {alpha: alpha, duration: duration})
        ]);
        return tl;
    }

    public blink(duration: number = 0.2): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const targetAlpha: number = 0.3;
        const timeInterVal: number = duration / 6;
        tl.to(this, {alpha: targetAlpha, duration: timeInterVal});
        tl.to(this, {alpha: 1, duration: timeInterVal});
        tl.to(this, {alpha: targetAlpha, duration: timeInterVal});
        tl.to(this, {alpha: 1, duration: timeInterVal});
        tl.to(this, {alpha: targetAlpha, duration: timeInterVal});
        tl.to(this, {alpha: 1, duration: timeInterVal});
        return tl;
    }

    public hide(duration: number = 0.3): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add([
            Game.enhancerView.hide(duration),
            Game.foregroundElements.hide(duration),
            gsap.to(this, {alpha: 0, duration: duration})
        ]);
        tl.add(() => {
            this.visible = true;
        });
        return tl;
    }

    public makeReelTopInLayer(reelId: number): void {
        this._reelContainer.addChild(this._reels[reelId]);
    }

    public reset(): void {
        this._reels.forEach((reel: PIXI.Container, reelId: number) => {
            this._reelContainer.addChild(reel);
        });
        this._reelContainer.addChild(this._reelContainer.mask as PIXI.Graphics);
    }

    public removeMask(): void {
        this._reelContainer.mask = null;
        this._reelMask.visible = false;
    }

    public addMask(): void {
        this._reelContainer.mask = this._reelMask;
        this._reelMask.visible = true;
    }

    private createReelContainers(): void {
        for (let i: number = 0; i < 5; i++) {
            const reelContainer: PIXI.Container = new PIXI.Container();
            reelContainer.name = "REEL_" + i;
            this._reels.push(reelContainer);
            this._reelContainer.addChild(reelContainer);
        }
        //first and last reel are down;
        this._reels[1].position.y = Config.SYMBOL_HEIGHT_HALF;
        this._reels[2].position.y = -Config.SYMBOL_HEIGHT_HALF;
        this._reels[3].position.y = Config.SYMBOL_HEIGHT_HALF;
    }

    private init() {
        this._frame = new PIXI.Sprite(Game.assetLoader.getTextures("reelbg/reelBackground")[0]);
        this._frame.name = "Frame";
        this._frame.anchor.set(0.5);
        this._frame.position.set(360, 416);
        this.addChild(this._frame);

        this._reelContainer = new PIXI.Container();
        this._reelContainer.name = "REEL_CONTAINER";
        this._reelContainer.position.set(0, 216);
        this.createReelContainers();
        this._reelMask = new PIXI.Graphics();
        this._reelMask.name = "REEL_MASK";
        this._reelContainer.addChild(this._reelMask);

        this._reelContainer.mask = this._reelMask;
        this._reelMask.position.set(0, -Config.SYMBOL_HEIGHT_HALF);
        this._reelMask.beginFill();
        this._reelMask.lineTo(Config.SYMBOL_WIDTH, 0)
            .lineTo(Config.SYMBOL_WIDTH, Config.SYMBOL_HEIGHT_HALF)
            .lineTo(Config.SYMBOL_WIDTH * 2, Config.SYMBOL_HEIGHT_HALF)

            .lineTo(Config.SYMBOL_WIDTH * 2, 0)
            .lineTo(Config.SYMBOL_WIDTH * 3, 0)
            .lineTo(Config.SYMBOL_WIDTH * 3, Config.SYMBOL_HEIGHT_HALF)

            .lineTo(Config.SYMBOL_WIDTH * 4, Config.SYMBOL_HEIGHT_HALF)
            .lineTo(Config.SYMBOL_WIDTH * 4, 0)
            .lineTo(Config.SYMBOL_WIDTH * 5, 0)
            .lineTo(Config.SYMBOL_WIDTH * 5, Config.SYMBOL_HEIGHT * 3)
            .lineTo(0, Config.SYMBOL_HEIGHT * 3)
            .lineTo(0, 0);
        this._reelMask.endFill();
        this.addChild(this._reelContainer);

        this._reelModels = [];
        for (let i: number = 0; i < 5; i++) {
            const reel = new SimpleReelModel(i, this, true);
            reel.setReelSet(Config.DEFAULT_REEL_SET[i]);
            reel.STEP_DURATION = Config.STEP_DURATION;
            reel.stepSize = Config.SYMBOL_HEIGHT;
            reel.init(Game.model.data.reels[i].concat());

            reel.events?.on(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => {
                //Switches state of all symbols from spin state to normal, when the symbols bounce.
                for (let symbolData of reel.stopSymbols) {
                    (<SymbolContainer>symbolData.positionable).setState(Mental2SymbolStates.NORMAL);
                }
            });
            reel.events?.on(SimpleReelModelState.SPIN, (data: SimpleReelModelEventData) => {
                //Switches state of all symbols from whatever state to spin when reel wind up is done and the spinning starts.
                for (let symbolData of reel.stopSymbols) {
                    (<SymbolContainer>symbolData.positionable).setState(Mental2SymbolStates.SPIN);
                }
            });

            this._reelModels.push(reel);
        }
        this.pivot.set(this.width * 0.5, (this.height * 0.5) - Config.MARGIN_TOP);
        this.position.set(this.width * 0.5, this.height * 0.5);
        this.visible = false;
    }

    get reels(): PIXI.Container[] {
        return this._reels;
    }

    get reelModels(): SimpleReelModel[] {
        return this._reelModels;
    }
}